import { useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut, Siren, CalendarPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FormDialog, fmtDT, useData } from "@/ops/ui";
import { getPosition } from "@/ops/filters";

const LEAVE = [{ k: "type", label: "Talep türü", type: "select", options: [["izin", "İzin"], ["rapor", "Sağlık raporu"], ["fazla_mesai", "Fazla mesai"]] },
  { k: "date_from", label: "Başlangıç", type: "date", required: true }, { k: "date_to", label: "Bitiş", type: "date", required: true },
  { k: "hours", label: "Saat (fazla mesai için)", type: "number" }, { k: "note", label: "Açıklama", type: "textarea" }];

function IncidentForm({ projects, onClose }) {
  const [v, setV] = useState({ project_id: projects[0]?.id || "", place: "", category: "Güvenlik ihlali", description: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(v).forEach(([k, x]) => fd.append(k, x));
    [...files].forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/ops/field/incidents", fd);
      toast.success(`Olay bildirildi: ${r.data.no}`);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="mt-3 grid gap-2" data-testid="incident-form">
      <select value={v.project_id} onChange={set("project_id")} className="field-input" data-testid="inc-project">{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
      <input required value={v.place} onChange={set("place")} placeholder="Olay yeri (örn. B kapısı otopark)" className="field-input" data-testid="inc-place" />
      <select value={v.category} onChange={set("category")} className="field-input" data-testid="inc-category">{["Güvenlik ihlali", "Hırsızlık", "Kavga / şiddet", "Yangın / acil durum", "Hasar", "Kayıp / buluntu", "Diğer"].map((c) => <option key={c}>{c}</option>)}</select>
      <textarea required value={v.description} onChange={set("description")} rows={4} placeholder="Ne oldu? Kimler ilgiliydi? Ne yapıldı?" className="field-input" data-testid="inc-desc" />
      <input type="file" multiple accept="image/jpeg,image/png,application/pdf" capture="environment" onChange={(e) => setFiles(e.target.files)} className="text-sm" data-testid="inc-files" />
      <button disabled={busy} className="btn-red justify-center !py-3" data-testid="inc-submit">{busy ? "Gönderiliyor..." : "Olayı Bildir"}</button>
    </form>
  );
}

const LEAVE_INITIAL = { type: "izin", hours: 0 };

async function currentPos() {
  try {
    return await getPosition();
  } catch (e) {
    toast.warning("Konum alınamadı; kayıt konumsuz olarak işaretlenecek.");
    return null;
  }
}

function useAttendance(pid, reload) {
  const [busy, setBusy] = useState(false);
  const act = async (dir) => {
    setBusy(true);
    const pos = await currentPos();
    try {
      await api.post(`/ops/field/attendance/${dir}`, { project_id: pid, lat: pos?.latitude ?? null, lng: pos?.longitude ?? null, accuracy: pos?.accuracy ?? null });
      toast.success(dir === "in" ? "İşe giriş kaydedildi" : "Çıkış kaydedildi");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return [busy, act];
}

function DutyStatus({ open }) {
  if (!open) return <p className="mt-1 text-sm text-zinc-500">Şu an açık giriş kaydınız yok.</p>;
  return <p className="mt-1 text-sm text-emerald-700" data-testid="duty-open">Görevdesiniz: {open.project_name} · Giriş {fmtDT(open.check_in_at)}</p>;
}

function AttendanceButton({ open, busy, pid, act }) {
  if (open) return <button disabled={busy} onClick={() => act("out")} className="btn-outline !py-2" data-testid="duty-out-btn"><LogOut className="h-4 w-4" /> Çıkış Yap</button>;
  return <button disabled={busy || !pid} onClick={() => act("in")} className="btn-red !py-2" data-testid="duty-in-btn"><LogIn className="h-4 w-4" /> İşe Giriş</button>;
}

function MyLists({ shifts, leaves }) {
  return (
    <>
      {shifts.length > 0 && <div className="mt-4"><p className="text-xs font-bold uppercase text-zinc-500">Vardiyalarım</p><ul className="text-sm">{shifts.map((s) => <li key={s.id}>{new Date(`${s.date}T00:00`).toLocaleDateString("tr-TR")} · {s.start}–{s.end} · {s.project_name}</li>)}</ul></div>}
      {leaves.length > 0 && <div className="mt-3"><p className="text-xs font-bold uppercase text-zinc-500">Taleplerim</p><ul className="text-sm">{leaves.slice(0, 5).map((l) => <li key={l.id}>{l.type_label} · {l.date_from} – {l.date_to} · <b>{l.status}</b></li>)}</ul></div>}
    </>
  );
}

export default function DutyPanel({ projects }) {
  const [d, reload] = useData("/ops/field/duty");
  const [pid, setPid] = useState(projects[0]?.id || "");
  const [leave, setLeave] = useState(false);
  const [inc, setInc] = useState(false);
  const [busy, act] = useAttendance(pid, reload);
  if (!d) return null;
  return (
    <div className="mb-6 rounded-lg border border-zinc-200 bg-white p-4" data-testid="duty-panel">
      <h2 className="font-bold">Giriş-Çıkış</h2>
      <DutyStatus open={d.open} />
      <div className="mt-3 flex flex-wrap gap-2">
        {!d.open && <select value={pid} onChange={(e) => setPid(e.target.value)} className="field-input max-w-xs" data-testid="duty-project">{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
        <AttendanceButton open={d.open} busy={busy} pid={pid} act={act} />
        <button onClick={() => setLeave(true)} className="btn-outline !py-2" data-testid="leave-request-btn"><CalendarPlus className="h-4 w-4" /> İzin / Mesai Talebi</button>
        <button onClick={() => setInc(!inc)} className="btn-outline !py-2" data-testid="incident-open-btn"><Siren className="h-4 w-4" /> Olay Bildir</button>
      </div>
      {inc && projects.length > 0 && <IncidentForm projects={projects} onClose={() => setInc(false)} />}
      <MyLists shifts={d.shifts} leaves={d.leaves} />
      <FormDialog open={leave} title="İzin / Fazla Mesai Talebi" fields={LEAVE} initial={LEAVE_INITIAL} onSubmit={(v) => api.post("/ops/field/leaves", { ...v, hours: Number(v.hours) || 0 }).then(() => toast.success("Talebiniz yöneticiye iletildi"))} onClose={(ok) => { setLeave(false); if (ok) reload(); }} />
    </div>
  );
}
